import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const CustomTooltip = ({ active, payload, label, platformMeta }) => {
  if (!active || !payload?.length) return null;

  const numberFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  });

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-3 text-xs text-left">
      <p className="text-xs uppercase text-muted-foreground mb-2">{label}</p>
      <div className="space-y-2">
        {payload.map((entry) => {
          const meta = platformMeta?.[entry?.dataKey];
          return (
            <div key={entry?.dataKey} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: entry?.stroke }}
                />
                <div>
                  <p className="text-sm font-medium text-foreground">{entry?.name}</p>
                  {meta?.friendlyName && (
                    <p className="text-[11px] text-muted-foreground">{meta.friendlyName}</p>
                  )}
                </div>
              </div>
              <span className="text-sm font-semibold text-foreground">
                {numberFormatter.format(entry?.value || 0)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PerformanceChart = ({ data, selectedPlatform, platformMeta }) => {
  const [hiddenLines, setHiddenLines] = useState([]);

  const platformConfig = {
    pinterest: { color: '#E60023', label: 'Pinterest' },
    wordpress: { color: '#21759B', label: 'WordPress' },
    facebook: { color: '#1877F2', label: 'Facebook' }
  };

  const toggleLine = (platform) => {
    setHiddenLines(prev => 
      prev?.includes(platform) 
        ? prev?.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const visiblePlatforms = selectedPlatform === 'all' 
    ? Object.keys(platformConfig)
    : [selectedPlatform];

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-foreground">Performance Trends</h2>
        <div className="flex items-center gap-4">
          {visiblePlatforms?.map(platform => (
            <button
              key={platform}
              onClick={() => toggleLine(platform)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                hiddenLines?.includes(platform)
                  ? 'bg-muted text-muted-foreground opacity-50'
                  : 'bg-card border border-border text-foreground'
              }`}
            >
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: platformConfig?.[platform]?.color }}
              />
              <span>{platformConfig?.[platform]?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="date" 
              stroke="#64748B"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#64748B"
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              content={(props) => <CustomTooltip {...props} platformMeta={platformMeta} />}
              cursor={{ stroke: '#CBD5F5', strokeWidth: 1 }}
            />
            <Legend />
            {visiblePlatforms?.map(platform => (
              !hiddenLines?.includes(platform) && (
                <Line
                  key={platform}
                  type="monotone"
                  dataKey={platform}
                  stroke={platformConfig?.[platform]?.color}
                  strokeWidth={2}
                  dot={{ fill: platformConfig?.[platform]?.color, r: 4 }}
                  activeDot={{ r: 6 }}
                  name={platformConfig?.[platform]?.label}
                />
              )
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PerformanceChart;
